import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Sheet,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Grid,
    AspectRatio,
    Stack
} from '@mui/joy';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import TimerIcon from '@mui/icons-material/Timer';
import GradeIcon from '@mui/icons-material/Grade';
import RemoveIcon from '@mui/icons-material/Remove';
import domain from '../utils/domain';
import { Registration, Student, Exam } from '../types/exam';

const ExamDetails: React.FC = () => {
    const navigate = useNavigate();
    const { examId, registrationHash } = useParams<{ examId: string; registrationHash: string }>();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [data, setData] = useState<{
        registration: Registration;
        student: Student;
        exam: Exam;
    } | null>(null);
    const fetchExamDetails = async () => {
        try {
            const response = await fetch(`${domain}/api/registrations/${examId}/${registrationHash}`);
            if (!response.ok) {
                throw new Error('Failed to fetch exam details');
            }
            const result = await response.json();
            setData(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load exam details');
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        fetchExamDetails();
    }, [examId, registrationHash]);



    const handleProceed = () => {
        //save the exam details and stuedent in the local storage
        localStorage.setItem('studentData', JSON.stringify(data));
        navigate(`/exam/proctoring/${examId}/${registrationHash}`);
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress size="lg" />
            </Box>
        );
    }

    if (error || !data) {
        return (
            <Box sx={{
                minHeight: '100vh',
                bgcolor: 'white',
                py: 4,
                px: 2
            }}>
                <Sheet
                    variant="outlined"
                    sx={{
                        maxWidth: '1000px',
                        margin: '0 auto',
                        borderRadius: 'lg',
                        overflow: 'hidden',
                        borderColor: '#ea7a23'
                    }}
                >
                    {/* Header */}
                    <Box sx={{
                        bgcolor: 'white',
                        p: 3,
                        height: '200px',
                        borderBottom: '1px solid',
                        borderColor: '#ea7a23',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                    }}>
                        <AspectRatio
                            ratio="3/1"
                            sx={{
                                width: 300,
                                margin: '0 auto',
                                mb: 2
                            }}
                        >
                            <img
                                src={`${process.env.PUBLIC_URL}/logo-vector.svg`}
                                alt="Vector India Logo"
                                style={{ objectFit: 'contain' }}
                            />
                        </AspectRatio>
                        <Typography
                            level="h3"
                            sx={{
                                textAlign: 'center',
                                color: '#ea7a23',
                                fontSize: '1.5rem'
                            }}
                        >
                            Exam Details
                        </Typography>
                    </Box>

                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Alert
                            color="warning"
                            variant="soft"
                            size="lg"
                            sx={{
                                mb: 4,
                                maxWidth: '600px',
                                margin: '0 auto',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2
                            }}
                        >
                            <Typography level="h4" sx={{ color: '#ea7a23', mb: 1 }}>
                                Invalid Exam URL
                            </Typography>
                            <Typography level="body-md">
                                The exam link you're trying to access appears to be invalid or has expired.
                                Please check your email for the correct exam link or contact support.
                            </Typography>
                            <Typography level="body-sm" sx={{ mt: 2 }}>
                                For assistance, please contact: <br />
                                Email: <a href="mailto:admissions@vectorindia.org">admissions@vectorindia.org</a><br />
                                Hyderabad: <a href="tel:+919866666699">+91 98666 66699</a> <br />
                                Bangalore: <a href="tel:+918762456789">+91 87624 56789</a><br />
                                Chennai: <a href="tel:+919444222459">+91 94442 22459</a><br />
                            </Typography>
                        </Alert>

                        <Button
                            size="lg"
                            onClick={() => window.location.href = 'https://www.vectorindia.org'}
                            sx={{
                                minWidth: 200,
                                bgcolor: '#ea7a23',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                mt: 4,
                                '&:hover': {
                                    bgcolor: '#d86a1f'
                                }
                            }}
                        >
                            Go to Home
                        </Button>
                    </Box>
                </Sheet>
            </Box>
        );
    }

    const { student, exam, registration } = data;

    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: 'white',
            py: 4,
            px: 2
        }}>
            <Sheet
                variant="outlined"
                sx={{
                    maxWidth: '1000px',
                    margin: '0 auto',
                    borderRadius: 'lg',
                    overflow: 'hidden',
                    borderColor: '#ea7a23'
                }}
            >
                {/* Header */}
                <Box sx={{
                    bgcolor: 'white',
                    p: 3,
                    height: '200px',
                    borderBottom: '1px solid',
                    borderColor: '#ea7a23',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                }}>
                    <AspectRatio
                        ratio="3/1"
                        sx={{
                            width: 300,
                            margin: '0 auto',
                            mb: 2
                        }}
                    >
                        <img
                            src={`${process.env.PUBLIC_URL}/logo-vector.svg`}
                            alt="Vector India Logo"
                            style={{ objectFit: 'contain' }}
                        />
                    </AspectRatio>
                    <Typography
                        level="h3"
                        sx={{
                            textAlign: 'center',
                            color: '#ea7a23',
                            fontSize: '1.5rem'
                        }}
                    >
                        {exam.title}
                    </Typography>
                </Box>

                <Box sx={{ p: 3 }}>
                    {registration.status === 'completed' && (
                        <Alert
                            color="warning"
                            variant="soft"
                            size="lg"
                            sx={{ mb: 4 }}
                        >
                            This exam has already been completed. Our team will contact you soon.
                        </Alert>
                    )}

                    <Grid container spacing={0} sx={{ minHeight: '250px' }}>
                        {/* Student Details */}
                        <Grid xs={12} md={6} sx={{
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            borderRight: { xs: 'none', md: `1px solid #ea7a23` }
                        }}>
                            <Box>
                                <Typography
                                    level="title-lg"
                                    sx={{
                                        mb: 3,
                                        color: '#ea7a23',
                                        fontSize: '1.5rem',
                                        fontWeight: 500
                                    }}
                                >
                                    Student Details
                                </Typography>
                                <Stack spacing={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <PersonIcon sx={{ color: '#ea7a23', fontSize: '1.2rem' }} />
                                        <Typography level="body-md" sx={{ fontSize: '1rem' }}>{student.name}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <EmailIcon sx={{ color: '#ea7a23', fontSize: '1.2rem' }} />
                                        <Typography level="body-md" sx={{ fontSize: '1rem' }}>{student.email}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <PhoneIcon sx={{ color: '#ea7a23', fontSize: '1.2rem' }} />
                                        <Typography level="body-md" sx={{ fontSize: '1rem' }}>{student.phone}</Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        </Grid>

                        {/* Exam Details */}
                        <Grid xs={12} md={6} sx={{
                            p: 2,
                            pl: { md: 6 },
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <Box>
                                <Typography
                                    level="title-lg"
                                    sx={{
                                        mb: 3,
                                        color: '#ea7a23',
                                        fontSize: '1.5rem',
                                        fontWeight: 500
                                    }}
                                >
                                    Exam Details
                                </Typography>
                                <Stack spacing={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <TimerIcon sx={{ color: '#ea7a23', fontSize: '1.2rem' }} />
                                        <Typography level="body-md" sx={{ fontSize: '1rem' }}>Duration: {exam.duration} minutes</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <GradeIcon sx={{ color: '#ea7a23', fontSize: '1.2rem' }} />
                                        <Typography level="body-md" sx={{ fontSize: '1rem' }}>Total Marks: {exam.total_marks}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <RemoveIcon sx={{ color: '#ea7a23', fontSize: '1.2rem' }} />
                                        <Typography level="body-md" sx={{ fontSize: '1rem' }}>Negative Marking: {exam.negative_marking} marks</Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        </Grid>
                    </Grid>

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 4
                    }}>
                        {registration.status !== 'completed' && (
                            <Button
                                size="lg"
                                onClick={handleProceed}
                                endDecorator={<ArrowForwardIcon />}
                                sx={{
                                    minWidth: 200,
                                    bgcolor: '#ea7a23',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    '&:hover': {
                                        bgcolor: '#d86a1f'
                                    }
                                }}
                            >
                                Proceed to Exam
                            </Button>
                        )}
                        {registration.status === 'completed' && (
                            <Typography level="body-sm" sx={{ mt: 2 }}>
                                For assistance, please contact: <br />
                                Email: <a href="mailto:admissions@vectorindia.org">admissions@vectorindia.org</a><br />
                                Hyderabad: <a href="tel:+919866666699">+91 98666 66699</a> <br />
                                Bangalore: <a href="tel:+918762456789">+91 87624 56789</a><br />
                                Chennai: <a href="tel:+919444222459">+91 94442 22459</a><br />
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Sheet>
        </Box>
    );
};

export default ExamDetails; 